import { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { tagService, Tag } from '../../services';
import { LoadingSpinner, ConfirmDialog } from '../ui';
import {
  AddButton,
  CloseButton,
  EmptyState,
  ErrorMessage,
  Form,
  ManagementContainer,
  Modal,
  ModalActions,
  ModalButton,
  ModalContent,
  ModalHeader,
  ModalTitle,
  SearchInput,
  Section,
  SectionHeader,
  SectionTitle,
} from './shared/AdminPrimitives';

const SearchContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const TagCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const TagHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TagIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.white};
`;

const TagInfo = styled.div`
  flex: 1;
`;

const TagName = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  word-break: break-word;
`;

const TagMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const TagStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const TagActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          
          &:hover {
            background-color: ${theme.colors.secondary};
          }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.gray.light};
          color: ${theme.colors.text};
          
          &:hover {
            background-color: ${theme.colors.gray.medium};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          
          &:hover {
            background-color: ${theme.colors.error}dd;
          }
        `;
      default:
        return '';
    }
  }}
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) => 
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary}20;
  }
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ArtworksModal = styled(Modal)``;

const ArtworksContent = styled(ModalContent)`
  max-width: 800px;
  max-height: 80vh;
`;

const ArtworksList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  max-height: 60vh;
  overflow-y: auto;
`;

const ArtworkItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ArtworkThumbnail = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const ArtworkInfo = styled.div`
  flex: 1;
`;

const ArtworkTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ArtworkMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const schema = yup.object({
  tag_name: yup.string().required('Tag name is required').max(255, 'Tag name must not exceed 255 characters'),
});

interface TagFormData {
  tag_name: string;
}

interface TagWithUsage extends Tag {
  usage_count?: number;
}

export function TagManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagWithUsage | null>(null);
  const [deleteTag, setDeleteTag] = useState<TagWithUsage | null>(null);
  const [viewingArtworks, setViewingArtworks] = useState<TagWithUsage | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<TagFormData>({
    resolver: yupResolver(schema),
  });

  // Fetch tags with usage count
  const { data: tagsData, isLoading } = useQuery({
    queryKey: ['admin', 'tags'],
    queryFn: () => tagService.getAdminTags({ includeUsageCount: true, limit: 100 }),
  });

  // Fetch artworks for a specific tag
  const { data: artworksForTag, isLoading: artworksLoading } = useQuery({
    queryKey: ['tag-artworks', viewingArtworks?.tag_id],
    queryFn: () => viewingArtworks ? tagService.getAdminArtworksForTag(viewingArtworks.tag_id) : Promise.resolve([]),
    enabled: !!viewingArtworks,
  });

  // Create tag mutation
  const createMutation = useMutation({
    mutationFn: (data: TagFormData) => tagService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tags'] });
      closeModal();
    },
  });

  // Update tag mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TagFormData }) => 
      tagService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tags'] });
      closeModal();
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => tagService.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tags'] });
      setDeleteTag(null);
    },
  });

  const openAddModal = () => {
    setEditingTag(null);
    reset({ tag_name: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (tag: TagWithUsage) => {
    setEditingTag(tag);
    setValue('tag_name', tag.tag_name);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTag(null);
    reset();
  };

  const onSubmit = async (data: TagFormData) => {
    if (editingTag) {
      await updateMutation.mutateAsync({ id: editingTag.tag_id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = async () => {
    if (deleteTag) {
      await deleteMutation.mutateAsync(deleteTag.tag_id);
    }
  };

  const handleViewArtworks = (tag: TagWithUsage) => {
    setViewingArtworks(tag);
  };

  const filteredTags = tagsData?.tags?.filter(tag =>
    tag.tag_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getTagInitial = (tagName: string) => {
    return tagName.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <ManagementContainer>
        <Section>
          <SectionHeader>
            <SectionTitle>Tag Management</SectionTitle>
            <AddButton onClick={openAddModal}>+ Add Tag</AddButton>
          </SectionHeader>

          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search tags by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <LoadingSpinner size="lg" text="Loading tags..." />
            </div>
          ) : filteredTags.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🏷️</EmptyIcon>
              <h3>No Tags Found</h3>
              <p>
                {searchTerm 
                  ? 'No tags match your search criteria.' 
                  : 'No tags have been created yet. Add your first tag to get started!'
                }
              </p>
            </EmptyState>
          ) : (
            <TagsGrid>
              {filteredTags.map((tag) => (
                <TagCard key={tag.tag_id}>
                  <TagHeader>
                    <TagIcon>
                      {getTagInitial(tag.tag_name)}
                    </TagIcon>
                    <TagInfo>
                      <TagName>{tag.tag_name}</TagName>
                      <TagMeta>
                        Tag ID: {tag.tag_id}
                      </TagMeta>
                    </TagInfo>
                  </TagHeader>

                  <TagStats>
                    <StatItem>
                      <StatValue>{tag.usage_count || 0}</StatValue>
                      <StatLabel>Artworks</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>0</StatValue>
                      <StatLabel>Exhibitions</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>
                        {tag.usage_count && tag.usage_count > 0 ? 'Active' : 'Unused'}
                      </StatValue>
                      <StatLabel>Status</StatLabel>
                    </StatItem>
                  </TagStats>

                  <TagActions>
                    <ActionButton 
                      $variant="secondary" 
                      onClick={() => handleViewArtworks(tag)}
                      disabled={!tag.usage_count || tag.usage_count === 0}
                    >
                      View ({tag.usage_count || 0})
                    </ActionButton>
                    <ActionButton onClick={() => openEditModal(tag)}>
                      Edit
                    </ActionButton>
                    <ActionButton
                      $variant="danger"
                      onClick={() => setDeleteTag(tag)}
                    >
                      Delete
                    </ActionButton>
                  </TagActions>
                </TagCard>
              ))}
            </TagsGrid>
          )}
        </Section>
      </ManagementContainer>

      {/* Add/Edit Modal */}
      <Modal $isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingTag ? 'Edit Tag' : 'Add New Tag'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Label htmlFor="tag_name">Tag Name *</Label>
              <Input
                {...register('tag_name')}
                id="tag_name"
                type="text"
                placeholder="Enter tag name"
                $hasError={!!errors.tag_name}
              />
              {errors.tag_name && (
                <ErrorMessage>{errors.tag_name.message}</ErrorMessage>
              )}
            </FormGroup>

            <ModalActions>
              <ModalButton type="button" $variant="secondary" onClick={closeModal}>
                Cancel
              </ModalButton>
              <ModalButton
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending 
                  ? 'Saving...' 
                  : editingTag ? 'Update Tag' : 'Add Tag'
                }
              </ModalButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>

      {/* View Artworks Modal */}
      <ArtworksModal $isOpen={!!viewingArtworks}>
        <ArtworksContent>
          <ModalHeader>
            <ModalTitle>
              Artworks with "{viewingArtworks?.tag_name}" ({viewingArtworks?.usage_count || 0})
            </ModalTitle>
            <CloseButton onClick={() => setViewingArtworks(null)}>×</CloseButton>
          </ModalHeader>

          {artworksLoading ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <LoadingSpinner size="md" text="Loading artworks..." />
            </div>
          ) : (
            <ArtworksList>
              {artworksForTag?.map((artwork: any) => (
                <ArtworkItem key={artwork.artwork_id}>
                  <ArtworkThumbnail>
                    🎨
                  </ArtworkThumbnail>
                  <ArtworkInfo>
                    <ArtworkTitle>
                      {artwork.title || `Artwork ${artwork.artwork_id}`}
                    </ArtworkTitle>
                    <ArtworkMeta>
                      {artwork.student_name && `by ${artwork.student_name} • `}
                      Uploaded {formatDate(artwork.uploaded_at)}
                    </ArtworkMeta>
                  </ArtworkInfo>
                </ArtworkItem>
              ))}
            </ArtworksList>
          )}

          <ModalActions>
            <ModalButton 
              type="button" 
              $variant="secondary" 
              onClick={() => setViewingArtworks(null)}
            >
              Close
            </ModalButton>
          </ModalActions>
        </ArtworksContent>
      </ArtworksModal>

      {/* Delete Confirmation */}
      {deleteTag && (
        <ConfirmDialog
          isOpen={!!deleteTag}
          title="Delete Tag"
          message={`Are you sure you want to delete the tag "${deleteTag.tag_name}"? This will automatically remove this tag from all ${deleteTag.usage_count || 0} associated artworks and exhibitions. This action cannot be undone.`}
          confirmText="Delete Tag"
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTag(null)}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      )}
    </>
  );
}
